/* eslint-disable no-console */
import React, { useCallback, useState } from "react";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import { useActions, useSelector } from "app/utils/hooks";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import Button from "app/components/Button/ButtonVariant";
import SegmentsLayout from "app/components/Layouts/SegmentsLayout";
import { useHistory } from "react-router-dom";
import TemplateSpaces from "./TemplatesList";
import { createSpaceTemplate } from "app/actions/spaceTemplate";
import { RootState } from "typedefs";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import TemplatesPaywall from "app/components/paywalls/Templates";
import { UpgradeToPro } from "app/components/paywalls";
import Navbar from "app/components/Navbar";

const SpaceTemplates = () => {
  const history = useHistory();
  const { self: user, pending } = useSelector(
    (state: RootState) => state.user,
    shallowEqual,
  );
  const templateActions = useActions({ createSpaceTemplate });
  const [newTemplateModalOpen, setNewTemplateModalOpen] =
    useState<boolean>(false);
  const [newTemplateName, setNewTemplateName] = useState<string | null>(null);
  const [newTemplateError, setNewTemplateError] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string>("");
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [showModal, setPaywallModal] = useState(false);
  const [showProPaywall, setShowProPaywall] = useState(false);

  const [canCreateTemplate] = useState(() => {
    if (!user || !user.planPermissions) {
      return null;
    }
    return user.planPermissions.includes("create_templates");
  });
  const { t } = useTranslation();

  const modalHandler = () => {
    setNewTemplateModalOpen((prev) => {
      if (prev) {
        setServerError("");
        setNewTemplateError(false);
        setNewTemplateName(null);
      }
      return !prev;
    });
  };

  const onNewTemplateClick = () => {
    if (canCreateTemplate) {
      modalHandler();
    } else {
      openPaywall();
    }
  };

  const openPaywall = useCallback(() => {
    setPaywallModal(true);
  }, []);

  const closePaywall = useCallback(() => {
    setPaywallModal(false);
  }, []);

  const paywallModalPro = useCallback(() => {
    setShowProPaywall(true);
  }, []);

  const closeProPaywall = useCallback(() => {
    setShowProPaywall(false);
  }, []);

  const onNewTemplateNameChange = (e: any) => {
    const name = e.target.value;
    setNewTemplateName(name);
    if (name && name.length > 0) {
      setNewTemplateError(false);
    } else {
      setNewTemplateError(true);
    }
    if (serverError) {
      setServerError("");
    }
  };

  const onSubmit = async () => {
    if (!canCreateTemplate) {
      paywallModalPro();
      modalHandler();
      return;
    }

    if (!newTemplateName || newTemplateName.trim().length === 0) {
      setNewTemplateError(true);
      return;
    }

    setServerError("");
    setIsCreating(true);

    try {
      const res = await templateActions.createSpaceTemplate({
        name: newTemplateName.trim(),
      });

      if (res.id) {
        history.push(`/space_templates/${res.id}/posts`);
        modalHandler();
      } else {
        console.error("space template creation response is missing 'id'.");
      }
    } catch (err) {
      if (err?.errors?.includes("name_already_taken")) {
        setServerError(t("This template name has already been taken"));
      } else {
        setServerError(t("Something went wrong. Please try again."));
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Navbar
        label={t("Templates")}
        hasSearch={false}
        hasNotifications={false}
      >
        <Button
          title={t("New Template")}
          icon={<i className="ico ico-add" />}
          buttonType="secondary-filled"
          childStyles={styles.create_btn_inner}
          buttonStyles={styles.create_btn}
          onClickFunc={onNewTemplateClick}
        />
      </Navbar>

      <div className={styles.mobile_navbar}>
        <h3 className={styles.h3_title}>Templates</h3>
        <Button
          title={t("New Template")}
          icon={<i className="ico ico-add" />}
          buttonType="secondary-filled"
          childStyles={styles.create_btn_inner}
          buttonStyles={styles.create_btn}
          onClickFunc={onNewTemplateClick}
        />
      </div>

      <SegmentsLayout isLoading={pending}>
        <div className={styles.container}>
          <TemplateSpaces />
        </div>
        <TemplatesPaywall show={showModal} onClose={closePaywall} />
        <UpgradeToPro show={showProPaywall} onClose={closeProPaywall} />
        <ModalLayout
          classNames={styles.modal_container}
          isOpen={newTemplateModalOpen}
          onClose={modalHandler}
        >
          <div className={styles.modal_content_wrapper}>
            <div className={styles.modal_header}>
              <h2 className={styles.modal_title}>{t("New Template")}</h2>
              <button
                type="button"
                className={styles.close_button}
                onClick={modalHandler}
                aria-label={t("Close")}
              >
                <i className="ico ico-x" />
              </button>
            </div>
            <span className={styles.input_wrapper}>
              <label htmlFor="new-template">{t("Template Name")}</label>
              <input
                id="new-template"
                name="new-template"
                placeholder={t("Enter a name for your template")}
                onChange={(e) => onNewTemplateNameChange(e)}
                className={cls({
                  [styles.error]: newTemplateError || !!serverError,
                })}
              />
              {newTemplateError && (
                <p className={styles.error_text}>{t("Name Required")}</p>
              )}
              {!newTemplateError && serverError && (
                <p className={styles.error_text}>{serverError}</p>
              )}
            </span>
            <span className={styles.buttons_container}>
              <Button
                title={t("Cancel")}
                buttonType="secondary-outline"
                onClickFunc={modalHandler}
              />
              <Button
                title={t("Create Template")}
                buttonType="primary"
                onClickFunc={onSubmit}
                disabled={
                  isCreating ||
                  (newTemplateName && newTemplateName.length <= 0)
                }
              />
            </span>
          </div>
        </ModalLayout>
      </SegmentsLayout>
    </>
  );
};

export default SpaceTemplates;
